import { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import Popup from '@shared/components/Popup/Popup';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Logout } from 'assets/icons/Logout.svg';
import CloseButton from 'components/CloseButton';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import CreateSecurityCodeModal from 'modules/EndToEndEncryption/SecurityCode/CreateSecurityCodeModal/CreateSecurityCodeModal';

interface LogoutWarningModalProps {
  onLogout: () => void;
  onPinCreated?: () => void;
}

const LogoutWarningModal = create<LogoutWarningModalProps>(({ onLogout, onPinCreated }) => {
  const { t } = useTranslation();
  const modal = useModal(LogoutWarningModal);
  const createSecurityCodeModal = useModal(CreateSecurityCodeModal);

  const handleCreateSecurityCode = () => {
    createSecurityCodeModal.show({
      onSuccess: onPinCreated,
      showSetupDescription: true,
      onLogout,
    });
    modal.remove();
  };

  const handleLogoutAnyway = () => {
    onLogout();
    modal.remove();
  };

  return (
    <Popup isVisible closePopup={modal.remove} className="!mt-4 !pb-0 md:!mt-0 md:!pb-4">
      <Popup.FullScreenContainer className="relative flex min-h-0 max-h-[calc(100dvh-1rem)] flex-col gap-6 overflow-hidden border border-gray-30 !bg-gray-10 px-6 pt-6 pb-4 md:w-[512px] md:max-h-[100dvh] md:gap-8 md:rounded-2xl md:py-12 md:shadow-[0px_24px_48px_0px_rgba(87,97,117,0.16)]">
        <CloseButton variant="ghost" onClick={modal.remove} className="absolute right-[15px] top-[15px] z-10" />
        <div className="flex min-h-0 flex-1 flex-col items-center gap-8 overflow-y-auto overscroll-contain px-6">
          <GradientIconWithShadow icon={Logout} bgVariant="tertiary" iconVariant="alert" iconClassName="!stroke-1.5" />
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="text-xl font-semibold leading-[1.25] text-black">
              {t('verification.logout_warning_title')}
            </h2>
            <p className="text-black">{t('verification.logout_warning_no_recovery')}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-4 pb-6 pt-2 sm:flex-row sm:justify-center md:pb-0">
          <Button variant="secondaryTertiaryOnGray" size="md" onClick={handleLogoutAnyway} className="w-full sm:w-auto">
            {t('verification.logout_anyway')}
          </Button>
          <Button variant="primary" size="md" onClick={handleCreateSecurityCode} className="w-full sm:w-auto">
            {t('verification.create_security_code')}
          </Button>
        </div>
      </Popup.FullScreenContainer>
    </Popup>
  );
});

export default LogoutWarningModal;
